import { useCallback } from 'react';
import type { Anomaly } from '@soc/shared';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { openInvestigation } from './investigationSlice';
import { clearSelection } from './mapUiSlice';

/** Rules whose backend supports an investigate drill-down (mirrors implemented strategies). */
export const INVESTIGABLE_RULES = new Set([
  'tac-lac-anomaly',
  'tac-lac-jump',
  'mcc-anomaly',
  'cell-location-mismatch',
  'site-tech-mismatch',
]);

/**
 * The anomaly id is `${ruleId}:${siteKey}:${key}:${windowFrom}` — windowFrom is a
 * ClickHouse DateTime ('YYYY-MM-DD HH:MM:SS') that itself contains colons, so join
 * everything from the 4th colon-group onward.
 */
function windowFromOfAlert(alert: Anomaly): string | null {
  const parts = alert.id.split(':');
  if (parts.length < 4) return null;
  const chDateTime = parts.slice(3).join(':'); // 'YYYY-MM-DD HH:MM:SS'
  const ms = Date.parse(chDateTime.replace(' ', 'T') + 'Z');
  return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
}

/**
 * The one place that opens Investigate Mode for an anomaly — shared by the map
 * popup, the feed card and the cluster popup so the trigger is identical
 * everywhere. Returns `canInvestigate` (is this rule supported?) and a memoized
 * `investigate()` that resolves the window/params and dispatches openInvestigation.
 */
export function useInvestigateAlert(alert: Anomaly): {
  canInvestigate: boolean;
  investigate: () => void;
} {
  const dispatch = useAppDispatch();
  const runtime = useAppSelector((s) => s.detection.runtime[alert.ruleId]);
  const params = useAppSelector((s) => s.rulesUi.params[alert.ruleId]);
  const canInvestigate = INVESTIGABLE_RULES.has(alert.ruleId);

  const investigate = useCallback(() => {
    // Window: `from` from the anomaly id (the exact window that produced it), or
    // the rule's runtime window as a fallback; `to` = the runtime cursor/now.
    const now = new Date().toISOString();
    const from =
      windowFromOfAlert(alert) ??
      runtime?.custom?.from ??
      new Date(Date.now() - (runtime?.windowMs ?? 3_600_000)).toISOString();
    const to = runtime?.custom?.to ?? runtime?.cursorIso ?? now;
    dispatch(clearSelection());
    dispatch(
      openInvestigation({
        anomalyId: alert.id,
        ruleId: alert.ruleId,
        towerId: alert.towerId,
        from,
        to,
        params,
      }),
    );
  }, [alert, runtime, params, dispatch]);

  return { canInvestigate, investigate };
}
